/**
 * TenantReportPage — éditeur du « Rapport de locataire » DG d'une enquête de crédit : pré-remplissage IA depuis le PDF du fournisseur,
 * sections à remplir (sommaire, demande, crédit, archives, identité, page pub), aperçu PDF, génération + envoi au propriétaire.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Download, Eye, Loader2, Save, Send, Sparkles, Star } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { Btn, Modal, errMsg } from "../../comptabilite/_shared";
import { SECTIONS, completion, getPath, setPath } from "./_report";
import SectionForm from "./SectionForm";
import PublishDialog from "./PublishDialog";

const base = (id) => `${API}/admin/pms/credit-checks/${id}/report`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }) : "");

export default function TenantReportPage() {
  const { checkId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [saved, setSaved] = useState("");
  const [section, setSection] = useState(SECTIONS[0].id);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const fileRef = useRef(null);
  const { setTrail } = useBreadcrumb();

  useEffect(() => {
    setTrail([{ label: "Files PMS", href: "/admin/pms-queue" }, { label: data?.check?.candidate ? `Rapport de locataire — ${data.check.candidate}` : "Rapport de locataire", href: `/admin/pms-queue/enquete/${checkId}/rapport` }]);
    return () => setTrail(null);
  }, [checkId, data?.check?.candidate, setTrail]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(base(checkId), { headers: authH() });
      setData(r.data); setReport(r.data.report); setSaved(JSON.stringify(r.data.report));
    } catch (e) { toast.error(errMsg(e, "Chargement impossible")); navigate("/admin/pms-queue"); }
  }, [checkId, navigate]);
  useEffect(() => { load(); }, [load]);

  const dirty = report && JSON.stringify(report) !== saved;
  const sec = useMemo(() => SECTIONS.find((s) => s.id === section) || SECTIONS[0], [section]);

  const save = async (silent = false) => {
    if (!report) return null;
    setBusy("save");
    try {
      const r = await axios.put(base(checkId), { report }, { headers: authH() });
      setReport(r.data.report); setSaved(JSON.stringify(r.data.report)); setData((d) => ({ ...d, meta: r.data.meta }));
      if (!silent) toast.success("Brouillon enregistré");
      return r.data.report;
    } catch (e) { toast.error(errMsg(e, "Enregistrement impossible")); return null; }
    finally { setBusy(""); }
  };

  const extract = async (file) => {
    if (!file) return;
    if (dirty && !window.confirm("Le pré-remplissage remplace les champs trouvés dans le PDF (vos autres saisies sont conservées). Continuer ?")) return;
    setBusy("extract");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${base(checkId)}/extract`, fd, { headers: { ...authH(), "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setReport(r.data.report); setSaved(JSON.stringify(r.data.report)); setData((d) => ({ ...d, meta: r.data.meta }));
      toast.success(`Rapport pré-rempli (${r.data.pages} pages lues${r.data.qr ? ", code QR conservé" : ""}) — vérifiez chaque section`);
    } catch (e) { toast.error(errMsg(e, "Pré-remplissage impossible")); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openPreview = async () => {
    if (dirty && !(await save(true))) return;
    setBusy("preview");
    try {
      const r = await axios.get(`${base(checkId)}/pdf`, { headers: authH(), responseType: "blob" });
      setPreview(URL.createObjectURL(r.data));
    } catch (e) { toast.error(errMsg(e, "Aperçu impossible")); }
    finally { setBusy(""); }
  };

  const publish = async (opts) => {
    if (dirty && !(await save(true))) return;
    setBusy("publish");
    try {
      const r = await axios.post(`${base(checkId)}/publish`, opts, { headers: authH() });
      toast.success(`Rapport déposé dans le coffre-fort${r.data.emailed ? " et envoyé par courriel" : opts.send_email ? ` — courriel non envoyé (${r.data.email_error || "erreur"})` : ""}`);
      setPublishing(false); load();
    } catch (e) { toast.error(errMsg(e, "Envoi impossible")); }
    finally { setBusy(""); }
  };

  const computeRatios = () => {
    const n = (v) => { const x = parseFloat(String(v ?? "").replace(/[^0-9.,-]/g, "").replace(",", ".")); return Number.isFinite(x) ? x : null; };
    const annual = n(getPath(report, "summary.household_income")) || n(getPath(report, "summary.personal_income")) || n(getPath(report, "application.household_income")) || n(getPath(report, "application.personal_income"));
    if (!annual) return toast.error("Indiquez d'abord le revenu du ménage (ou personnel) dans le résumé du demandeur");
    const monthly = annual / 12;
    let next = report;
    const debt = n(getPath(report, "scores.debt_monthly")); const rent = n(getPath(report, "header.rent"));
    if (debt != null) next = setPath(next, "scores.debt_ratio_pct", String(Math.round((debt / monthly) * 100)));
    if (rent != null) next = setPath(next, "scores.rent_ratio_pct", String(Math.round((rent / monthly) * 100)));
    setReport(next); toast.success("Ratios calculés");
  };

  const saveAdDefault = async () => {
    setBusy("ad");
    try { await axios.put(`${API}/admin/pms/tenant-report/ad`, { ad: report.ad }, { headers: authH() }); toast.success("Texte de la page pub enregistré par défaut pour les prochains rapports"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data || !report) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" /></div>;
  const { check, meta } = data;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 space-y-4" data-testid="tenant-report-page">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => navigate("/admin/pms-queue")} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="tr-back"><ArrowLeft className="w-3.5 h-3.5" /> Files PMS</button>
        <h1 className="text-lg sm:text-xl font-bold text-slate-900 w-full sm:w-auto" data-testid="tr-title">Rapport de locataire — {check.candidate}</h1>
        <span className="text-xs text-slate-500">Propriétaire : <b>{check.client_name || "—"}</b> · dossier {check.master_name || "—"}</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-wrap items-center gap-2" data-testid="tr-toolbar">
        <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => extract(e.target.files?.[0])} data-testid="tr-extract-input" />
        <Btn tone="sky" icon={Sparkles} onClick={() => fileRef.current?.click()} busy={busy === "extract"} testId="tr-extract-btn" title="Déposez le PDF du fournisseur : le texte est lu et les sections pré-remplies par IA (≈ 1 minute)">Pré-remplir depuis un PDF</Btn>
        <Btn tone="slate" icon={Save} onClick={() => save(false)} busy={busy === "save"} disabled={!dirty} testId="tr-save-btn">{dirty ? "Enregistrer le brouillon" : "Brouillon enregistré"}</Btn>
        <Btn tone="slate" icon={Eye} onClick={openPreview} busy={busy === "preview"} testId="tr-preview-btn">Aperçu PDF</Btn>
        <Btn icon={Send} onClick={() => setPublishing(true)} testId="tr-publish-btn" className="ml-auto">Générer et envoyer</Btn>
        <div className="w-full text-[11px] text-slate-500 flex flex-wrap gap-x-3" data-testid="tr-meta">
          {meta?.ai_at && <span>Pré-rempli par IA le {fmt(meta.ai_at)}{meta.source_pdf_name ? ` à partir de « ${meta.source_pdf_name} »` : ""}{report.qr_png_b64 ? " · code QR conservé" : ""}</span>}
          {meta?.updated_at && <span>Brouillon enregistré le {fmt(meta.updated_at)}</span>}
          {check.report_generated_at && <span className="text-emerald-700 font-semibold">Rapport envoyé le {fmt(check.report_generated_at)}</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-3 px-3 lg:mx-0 lg:px-0 lg:sticky lg:top-4 self-start" data-testid="tr-nav">
          {SECTIONS.map((s) => { const pc = completion(report, s); return (
            <button key={s.id} onClick={() => setSection(s.id)} data-testid={`tr-nav-${s.id}`}
              className={`shrink-0 text-left px-3 py-2 rounded-xl text-xs font-semibold border transition flex items-center gap-2 ${section === s.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${pc >= 70 ? "bg-emerald-500" : pc >= 30 ? "bg-amber-400" : "bg-slate-300"}`} />
              <span className="flex-1">{s.label}</span>
              <span className={`text-[10px] ${section === s.id ? "text-slate-300" : "text-slate-400"}`}>{pc} %</span>
            </button>); })}
        </nav>
        <div>
          <SectionForm section={sec} report={report} setReport={setReport} onComputeRatios={computeRatios} />
          {sec.adDefault && <div className="mt-3 flex justify-end"><Btn tone="slate" icon={Star} onClick={saveAdDefault} busy={busy === "ad"} testId="tr-ad-default-btn">Enregistrer comme texte par défaut</Btn></div>}
        </div>
      </div>

      {busy === "extract" && (
        <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3 text-center p-6" data-testid="tr-extract-overlay">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
          <p className="font-semibold text-slate-800">Lecture du PDF et pré-remplissage par IA…</p>
          <p className="text-xs text-slate-500 max-w-md">Environ une minute. Le texte des pages du rapport est transcrit dans chaque section ; le code QR de vérification est conservé. Les pièces jointes (talons de paie, permis) ne sont pas lues.</p>
        </div>
      )}
      {preview && (
        <Modal title="Aperçu du rapport" sub="Document généré à partir du brouillon enregistré" onClose={() => { URL.revokeObjectURL(preview); setPreview(null); }} wide testId="tr-preview-modal"
          footer={<><a href={preview} download={`Rapport-de-locataire-${check.candidate.replace(/\s+/g, "-")}.pdf`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="tr-preview-download"><Download className="w-3.5 h-3.5" /> Télécharger</a>
            <Btn tone="slate" onClick={() => { URL.revokeObjectURL(preview); setPreview(null); }} testId="tr-preview-close-btn">Fermer</Btn></>}>
          <iframe title="Aperçu PDF" src={preview} className="w-full h-[75vh] rounded-xl border border-slate-200" data-testid="tr-preview-frame" />
          <p className="text-[11px] text-slate-500">Si l'aperçu ne s'affiche pas dans votre navigateur, <a href={preview} target="_blank" rel="noreferrer" className="text-teal-700 underline" data-testid="tr-preview-open">ouvrez-le dans un nouvel onglet</a> ou téléchargez-le.</p>
        </Modal>
      )}
      {publishing && <PublishDialog check={check} onClose={() => setPublishing(false)} onConfirm={publish} busy={busy === "publish"} />}
    </div>
  );
}
